import {
  Container
} from "./chunk-GQ3RJ3UX.mjs";

// src/MapHero/MapHero.tsx
import { useState } from "react";
import * as CountUpModule from "react-countup";
import * as VisibilitySensorModule from "react-visibility-sensor";
import Box2 from "@mui/material/Box/index.js";
import Grid from "@mui/material/Grid/index.js";
import Typography from "@mui/material/Typography/index.js";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

// src/MapHero/EuropeMap.tsx
import { useEffect, useMemo } from "react";
import Box from "@mui/material/Box/index.js";
import { useTheme } from "@mui/material/styles/index.js";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapContainer, ImageOverlay, Marker, useMap } from "react-leaflet";
import { jsx, jsxs } from "react/jsx-runtime";
var MAP_WIDTH = 942;
var MAP_HEIGHT = 780;
var IMAGE_BOUNDS = [
  [0, 0],
  [MAP_HEIGHT, MAP_WIDTH]
];
var InvalidateOnResize = () => {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    const observer = new ResizeObserver(() => map.invalidateSize());
    observer.observe(container);
    return () => observer.disconnect();
  }, [map]);
  return null;
};
var EuropeMap = ({ pins: pins2, imageSrc }) => {
  const theme = useTheme();
  const pinColor = theme.palette.primary.main;
  const pinIcon = useMemo(
    () => L.divIcon({
      className: "akyga-map-pin-wrapper",
      html: '<span class="akyga-map-pin"></span>',
      iconSize: [15, 15],
      iconAnchor: [7, 7]
    }),
    []
  );
  const handlePinClick = (id) => {
    document.getElementById(`distributor-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return /* @__PURE__ */ jsx(
    Box,
    {
      sx: {
        position: "relative",
        width: MAP_WIDTH,
        maxWidth: "100%",
        mx: "auto",
        aspectRatio: `${MAP_WIDTH} / ${MAP_HEIGHT}`,
        borderRadius: 2,
        overflow: "hidden",
        ".leaflet-container": {
          width: "100%",
          height: "100%",
          background: "transparent"
        },
        // Leaflet's div-icon ships a default white box + border; strip it
        // so only our own pulsing dot markup is visible.
        ".akyga-map-pin-wrapper": {
          background: "transparent",
          border: "none"
        },
        ".akyga-map-pin": {
          display: "block",
          width: 15,
          height: 15,
          borderRadius: "50%",
          cursor: "pointer",
          backgroundColor: pinColor,
          animation: "akyga-pin-pulse 1.5s infinite"
        },
        "@keyframes akyga-pin-pulse": {
          "0%": { boxShadow: `0 0 0 0 ${pinColor}70` },
          "70%": { boxShadow: `0 0 0 10px ${pinColor}00` },
          "100%": { boxShadow: `0 0 0 0 ${pinColor}00` }
        },
        ".leaflet-tooltip.akyga-map-tooltip": {
          borderRadius: "8px",
          padding: "8px 14px",
          fontWeight: 600,
          fontSize: "0.8rem",
          color: theme.palette.text.primary,
          border: "none"
        }
      },
      children: /* @__PURE__ */ jsxs(
        MapContainer,
        {
          crs: L.CRS.Simple,
          bounds: IMAGE_BOUNDS,
          maxBounds: IMAGE_BOUNDS,
          maxBoundsViscosity: 1,
          minZoom: -2,
          maxZoom: 2,
          zoomSnap: 0.25,
          zoomControl: true,
          attributionControl: false,
          scrollWheelZoom: true,
          children: [
            /* @__PURE__ */ jsx(InvalidateOnResize, {}),
            /* @__PURE__ */ jsx(ImageOverlay, { url: imageSrc, bounds: IMAGE_BOUNDS }),
            pins2.map((pin) => /* @__PURE__ */ jsx(
              Marker,
              {
                position: [MAP_HEIGHT - parseInt(pin.y, 10), parseInt(pin.x, 10)],
                icon: pinIcon,
                eventHandlers: { click: () => handlePinClick(pin.id) },
                ref: (markerInstance) => {
                  if (markerInstance && !markerInstance.getTooltip()) {
                    markerInstance.bindTooltip(pin.info, {
                      direction: "top",
                      offset: [0, -10],
                      className: "akyga-map-tooltip"
                    });
                  }
                }
              },
              pin.id
            ))
          ]
        }
      )
    }
  );
};
var EuropeMap_default = EuropeMap;

// src/MapHero/pins.ts
var pins = [
  // --- POLSKA (Wszystko idealnie w granicach Polski) ---
  { id: 1, x: "575", y: "345", info: "BOTLAND (Bralin, Poland)" },
  {
    id: 2,
    x: "595",
    y: "325",
    info: "Transfer Multisort Elektronik Sp. z o.o. (\u0141\xF3d\u017A, Poland)"
  },
  { id: 3, x: "615", y: "305", info: "Firma Piekarz Sp.J. (Warszawa, Poland)" },
  { id: 4, x: "610", y: "360", info: "AUDIO-DESIGN (Krak\xF3w, Poland)" },
  {
    id: 21,
    x: "585",
    y: "340",
    info: "Elektronik s.c. (Ostr\xF3w Wielkopolski, Poland)"
  },
  // --- LITWA (Wilno) ---
  {
    id: 5,
    x: "675",
    y: "225",
    info: "Vygento Li\u010Dio firma Evita / Lemona (Vilnius, Lithuania)"
  },
  // --- DANIA & SZWECJA (Skandynawia) ---
  {
    id: 7,
    x: "467",
    y: "230",
    info: "Agora Sourcing ApS (Kongens Lyngby, Denmark)"
  },
  { id: 22, x: "495", y: "215", info: "Electrokit Sweden AB (Malm\xF6, Sweden)" },
  // --- NIEMCY ---
  { id: 8, x: "485", y: "335", info: "TME Germany GmbH (Leipzig, Germany)" },
  // --- BENELUX & UK ---
  { id: 9, x: "355", y: "335", info: "Vanmed (Brugge, Belgium)" },
  {
    id: 10,
    x: "320",
    y: "350",
    info: "Transfer Multisort Elektronik B.V. (Eindhoven, Netherlands)"
  },
  {
    id: 11,
    x: "230",
    y: "300",
    info: "Transfer Multisort Elektronik Ltd. (Birmingham, UK)"
  },
  // --- EUROPA POŁUDNIOWA (Hiszpania & Włochy) ---
  {
    id: 13,
    x: "145",
    y: "645",
    info: "Transfer Multisort Elektronik S.L.U. (Madrid, Spain)"
  },
  { id: 12, x: "420", y: "520", info: "TME ITALIA S.r.l. (Grassobbio, Italy)" },
  // --- CZECHY (Środek i wschód kraju) ---
  {
    id: 14,
    x: "530",
    y: "385",
    info: "BATTEX spol. s r.o. (Slan\xFD, Czech Republic)"
  },
  {
    id: 16,
    x: "575",
    y: "380",
    info: "TME Czech Republic, s.r.o. (Ostrawa, Czech Republic)"
  },
  // --- SŁOWACJA ---
  {
    id: 17,
    x: "600",
    y: "415",
    info: "TME Slovakia, s.r.o. (\u017Dilina, Slovakia)"
  },
  // --- WĘGRY ---
  { id: 18, x: "605", y: "455", info: "TME Hungary Kft. (Budapest, Hungary)" },
  // --- RUMUNIA (Wypchnięte głębiej na terytorium Rumunii) ---
  {
    id: 15,
    x: "655",
    y: "480",
    info: "Transfer Multisort Elektronik S.R.L. (Timi\u0219oara, Romania)"
  },
  { id: 19, x: "685", y: "490", info: "Sprinter 2000 S.A. (Brasov, Romania)" },
  // --- GRECJA ---
  { id: 20, x: "715", y: "625", info: "Batteryworld (Thessaloniki, Greece)" }
];

// src/MapHero/MapHero.tsx
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var mock = [
  { title: 22, subtitle: "mapHero.stats.0.subtitle", suffix: "" },
  { title: 14, subtitle: "mapHero.stats.2.subtitle", suffix: "" }
];
var fadeInVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};
var unwrapCjsDefault = (value) => typeof value === "function" ? value : value.default;
var CountUp = unwrapCjsDefault(CountUpModule.default);
var VisibilitySensor = unwrapCjsDefault(VisibilitySensorModule.default);
var LeftSide = () => {
  const { t } = useTranslation();
  const [viewPortEntered, setViewPortEntered] = useState(false);
  const setViewPortVisibility = (isVisible) => {
    if (viewPortEntered) {
      return;
    }
    setViewPortEntered(isVisible);
  };
  return /* @__PURE__ */ jsxs2(Box2, { children: [
    /* @__PURE__ */ jsx2(Typography, { variant: "h3", sx: { fontWeight: "medium" }, gutterBottom: true, children: t("mapHero.globalReach") }),
    /* @__PURE__ */ jsxs2(Box2, { marginBottom: 2, children: [
      /* @__PURE__ */ jsx2(Typography, { variant: "h4", sx: { fontWeight: 700 }, gutterBottom: true, children: t("mapHero.heading") }),
      /* @__PURE__ */ jsx2(Typography, { color: "text.secondary", children: t("mapHero.description") })
    ] }),
    /* @__PURE__ */ jsx2(Grid, { container: true, spacing: 2, children: mock.map((item, i) => /* @__PURE__ */ jsxs2(Grid, { item: true, xs: 12, md: 4, children: [
      /* @__PURE__ */ jsx2(Typography, { color: "primary", variant: "h3", gutterBottom: true, children: /* @__PURE__ */ jsx2(Box2, { fontWeight: 600, children: /* @__PURE__ */ jsx2(
        VisibilitySensor,
        {
          onChange: (isVisible) => setViewPortVisibility(isVisible),
          delayedCall: true,
          children: /* @__PURE__ */ jsx2(
            CountUp,
            {
              duration: 2,
              end: viewPortEntered ? item.title : 0,
              start: 0,
              suffix: item.suffix
            }
          )
        }
      ) }) }),
      /* @__PURE__ */ jsx2(Typography, { color: "text.secondary", component: "p", children: t(item.subtitle) })
    ] }, i)) })
  ] });
};
var RightSide = ({ mapImageSrc }) => /* @__PURE__ */ jsx2(
  Box2,
  {
    sx: {
      display: "flex",
      justifyContent: "flex-end",
      paddingRight: { xs: 0, md: "2vw" },
      paddingTop: { xs: "5vh", md: 0 },
      width: "100%"
    },
    children: /* @__PURE__ */ jsx2(
      Box2,
      {
        component: motion.div,
        initial: "hidden",
        animate: "visible",
        transition: { duration: 0.5, delay: 0.4 },
        variants: fadeInVariants,
        sx: { width: "100%", maxWidth: "100%" },
        children: /* @__PURE__ */ jsx2(EuropeMap_default, { pins, imageSrc: mapImageSrc })
      }
    )
  }
);
var MapHero = ({
  mapImageSrc = "/assets/europe_map.svg"
} = {}) => /* @__PURE__ */ jsxs2(
  Box2,
  {
    sx: {
      width: 1,
      height: 1,
      overflow: "hidden",
      paddingBottom: "1vh"
    },
    children: [
      /* @__PURE__ */ jsx2(Container, { maxWidth: { sm: 1, md: 1236 }, children: /* @__PURE__ */ jsx2(LeftSide, {}) }),
      /* @__PURE__ */ jsx2(Container, { maxWidth: { sm: 1, md: 1236 }, children: /* @__PURE__ */ jsx2(RightSide, { mapImageSrc }) })
    ]
  }
);
var MapHero_default = MapHero;
export {
  MapHero_default as MapHero
};
